import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FilePdf, Trash, Truck, PencilSimple } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TransportInvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/transport-invoices/${id}`);
        setInv(r.data);
      } catch (e) {
        if (e?.response?.status === 404) nav("/transport-invoices", { replace: true });
        else setError(e?.response?.data?.detail || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/transport-invoices/${id}/pdf`, { responseType: "blob" });
      const type = r.data.type || "";
      if (!type.includes("pdf") && r.data.size < 200) {
        throw new Error("Server did not return a PDF");
      }
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e?.message || "Failed to download PDF";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { const t = await blob.text(); const j = JSON.parse(t); msg = j.detail || t; } catch (_) { /* ignore */ }
      }
      const status = e?.response?.status;
      alert(`Could not download PDF${status ? ` (HTTP ${status})` : ""}: ${msg}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete transport invoice ${inv.invoice_no}?`)) return;
    try {
      await axios.delete(`${API}/transport-invoices/${id}`);
      nav("/transport-invoices");
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;
  if (error) return <div className="p-8 text-rose-600 text-sm">{error}</div>;
  if (!inv) return null;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="ti-detail-page">
      <button onClick={() => nav("/transport-invoices")}
        className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm mb-4">
        <ArrowLeft size={16} /> Back to list
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
            <Truck size={14} weight="fill" /> Transport Invoice
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 font-mono">{inv.invoice_no}</h1>
          <p className="text-sm text-slate-500 mt-1">Date {inv.invoice_date}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav(`/transport-invoices/${id}/edit`)} data-testid="ti-edit-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-slate-300 text-slate-800 text-sm font-bold hover:bg-slate-50">
            <PencilSimple size={18} /> Edit
          </button>
          <button onClick={downloadPdf} data-testid="ti-download-pdf"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-slate-900 text-white text-sm font-bold hover:bg-slate-800">
            <FilePdf size={18} weight="bold" /> Download PDF
          </button>
          <button onClick={handleDelete} data-testid="ti-delete-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-rose-200 text-rose-600 text-sm font-bold hover:bg-rose-50">
            <Trash size={18} /> Delete
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Consignor / Party</div>
        <div className="text-slate-900 font-bold text-lg">{inv.party_name}</div>
        <div className="text-slate-600 text-sm whitespace-pre-line">{inv.party_address}</div>
        {inv.party_gstin && (
          <div className="mt-2 text-xs text-slate-500 tracking-wide"><b className="text-slate-700">GSTIN:</b> {inv.party_gstin}</div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <th className="px-3 py-2 text-left w-10">Sr</th>
                <th className="px-3 py-2 text-left">CN No</th>
                <th className="px-3 py-2 text-left">From → To</th>
                <th className="px-3 py-2 text-left">Details</th>
                <th className="px-3 py-2 text-left">HSN</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(inv.items || []).map((it, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-3 py-2 font-mono text-slate-900">{it.cn_no}</td>
                  <td className="px-3 py-2 text-slate-700">{it.from_location} → {it.to_location}</td>
                  <td className="px-3 py-2 text-slate-600">{it.transport_details}</td>
                  <td className="px-3 py-2 text-slate-500 font-mono">{it.hsn_code}</td>
                  <td className="px-3 py-2 text-right font-mono">₹ {Number(it.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6">
        {inv.rcm && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-[11px] font-bold uppercase tracking-wider text-red-700" data-testid="ti-rcm-banner">
            Reverse Charge Mechanism (RCM) — Tax payable by recipient
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 max-w-sm ml-auto text-sm">
          <div className="text-slate-500">Subtotal</div>
          <div className="text-right font-mono">₹ {Number(inv.subtotal || 0).toFixed(2)}</div>
          {inv.gst_type === "no_gst" ? (
            <>
              <div className="text-slate-500 italic">Tax</div>
              <div className="text-right font-mono italic text-slate-400">Not applicable</div>
            </>
          ) : inv.gst_type === "igst" ? (
            <>
              <div className="text-slate-500">IGST</div>
              <div className="text-right font-mono">₹ {Number(inv.igst || 0).toFixed(2)}</div>
            </>
          ) : (
            <>
              <div className="text-slate-500">CGST</div>
              <div className="text-right font-mono">₹ {Number(inv.cgst || 0).toFixed(2)}</div>
              <div className="text-slate-500">SGST</div>
              <div className="text-right font-mono">₹ {Number(inv.sgst || 0).toFixed(2)}</div>
            </>
          )}
          {inv.rcm && inv.gst_type !== "no_gst" && (
            <>
              <div className="text-red-700 font-semibold text-xs">Tax payable by recipient (RCM)</div>
              <div className="text-right font-mono text-xs text-red-700" data-testid="ti-detail-rcm-tax">₹ {(Number(inv.cgst || 0) + Number(inv.sgst || 0) + Number(inv.igst || 0)).toFixed(2)}</div>
            </>
          )}
          <div className="col-span-2 border-t border-slate-200 mt-2 pt-2" />
          <div className="text-slate-900 font-bold">{inv.rcm ? "GRAND TOTAL (Taxable)" : "GRAND TOTAL"}</div>
          <div className="text-right font-mono font-black text-lg" data-testid="ti-detail-total">₹ {Number(inv.total || 0).toFixed(2)}</div>
        </div>
        {inv.amount_in_words && (
          <p className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600 italic">
            <b className="text-slate-800 not-italic">Amount in Words:</b> {inv.amount_in_words}
          </p>
        )}
        {inv.rcm && (
          <p className="mt-3 text-xs text-red-700 font-semibold" data-testid="ti-rcm-note">
            This invoice is under the Reverse Charge Mechanism (RCM), and the responsibility to pay the tax lies with the buyer or recipient.
          </p>
        )}
      </div>
    </div>
  );
}
